// frontend/src/pages/InviteAcceptPage.js
// 公開ページ：/invite/:token で開かれる、新規組織登録の招待受諾画面

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import TurnstileWidget from '../components/TurnstileWidget';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
const SUPABASE_URL = 'https://tpxzotpgkcaioifgzlii.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InRweHpvdHBna2NhaW9pZmd6bGlpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njk0ODI4MTIsImV4cCI6MjA4NTA1ODgxMn0.Re0nm2Nu4BRFf73F9kchoZDjY8BpG1N0Qfe7BtzE1tE';

export default function InviteAcceptPage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [previewError, setPreviewError] = useState('');
  const [invite, setInvite] = useState(null);

  // フォーム
  const [employeeId, setEmployeeId] = useState('');
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [success, setSuccess] = useState(null);

  // 招待の事前確認
  useEffect(() => {
    const fetchPreview = async () => {
      try {
        const res = await fetch(`${SUPABASE_URL}/functions/v1/preview-invite`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'apikey': SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ token }),
        });
        const json = await res.json();
        if (!json.success) {
          setPreviewError(json.message || '招待の確認に失敗しました');
        } else {
          setInvite(json.invite);
        }
      } catch (e) {
        setPreviewError(e.message);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchPreview();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (password !== passwordConfirm) {
      setSubmitError('パスワードが一致しません');
      return;
    }
    if (password.length < 8) {
      setSubmitError('パスワードは 8 文字以上にしてください');
      return;
    }
    if (!captchaToken) {
      setSubmitError('セキュリティチェックを完了してください');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/accept-invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          token,
          employeeId,
          lastName,
          firstName,
          password,
          captchaToken,
        }),
      });
      const json = await res.json();
      if (!json.success) {
        setSubmitError(json.message || '登録に失敗しました');
        setCaptchaToken('');
        return;
      }
      setSuccess({
        organizationId: json.organizationId,
        employeeId: json.employeeId,
      });
    } catch (err) {
      setSubmitError(err.message);
      setCaptchaToken('');
    } finally {
      setSubmitting(false);
    }
  };

  // ============ 表示分岐 ============

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>読み込み中...</div>
      </div>
    );
  }

  if (previewError || !invite) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h1 style={styles.title}>招待が利用できません</h1>
          <p style={styles.error}>{previewError || '招待が見つかりません'}</p>
          <p style={styles.helper}>
            招待を発行した運営にお問い合わせください。
          </p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h1 style={styles.title}>登録完了</h1>
          <p style={styles.helper}>
            <strong>{invite.intended_org_name}</strong> の管理者アカウントを作成しました。
          </p>
          <div style={styles.successBox}>
            <div><strong>組織ID:</strong> {success.organizationId}</div>
            <div><strong>社員番号:</strong> {success.employeeId}</div>
            <div><strong>パスワード:</strong> 入力したもの</div>
          </div>
          <p style={styles.helper}>
            ログイン画面でこの 3 つを入力してご利用開始ください。
          </p>
          <button
            onClick={() => navigate('/')}
            style={styles.button}
          >
            ログイン画面へ
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h1 style={styles.title}>ScrubEdge 招待</h1>
        <p style={styles.helper}>
          以下の組織の管理者アカウントを作成します。
        </p>

        <div style={styles.previewBox}>
          <div style={styles.previewRow}><span style={styles.previewLabel}>組織名</span><span>{invite.intended_org_name}</span></div>
          <div style={styles.previewRow}><span style={styles.previewLabel}>組織ID</span><span style={{ fontFamily: 'monospace' }}>{invite.intended_org_slug}</span></div>
          <div style={styles.previewRow}><span style={styles.previewLabel}>管理者メール</span><span>{invite.intended_admin_email}</span></div>
          <div style={styles.previewRow}><span style={styles.previewLabel}>プラン</span><span>{invite.intended_plan}</span></div>
        </div>

        {submitError && <div style={styles.error}>{submitError}</div>}

        <form onSubmit={handleSubmit}>
          <label style={styles.label}>社員番号（管理者用、ログインで使う）</label>
          <input
            type="text"
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            required
            placeholder="例: 1000"
            style={styles.input}
          />

          <div style={styles.row}>
            <div style={{ flex: 1 }}>
              <label style={styles.label}>姓</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                style={styles.input}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={styles.label}>名</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                style={styles.input}
              />
            </div>
          </div>

          <label style={styles.label}>パスワード（8 文字以上）</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            style={styles.input}
          />

          <label style={styles.label}>パスワード（確認）</label>
          <input
            type="password"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            required
            minLength={8}
            style={styles.input}
          />

          <div style={styles.turnstile}>
            <TurnstileWidget
              siteKey={TURNSTILE_SITE_KEY}
              onVerify={(t) => setCaptchaToken(t)}
              onExpire={() => setCaptchaToken('')}
              onError={() => setCaptchaToken('')}
            />
          </div>

          <button
            type="submit"
            disabled={submitting || !captchaToken}
            style={{ ...styles.button, ...(submitting || !captchaToken ? styles.buttonDisabled : {}) }}
          >
            {submitting ? '作成中...' : '招待を受ける'}
          </button>
        </form>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1a365d 0%, #2563eb 100%)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '2rem 1rem',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  card: {
    background: '#fff',
    borderRadius: '16px',
    padding: '2.5rem 2rem',
    width: '100%',
    maxWidth: '500px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#0f172a',
    margin: '0 0 0.5rem 0',
    textAlign: 'center',
  },
  helper: {
    fontSize: '0.95rem',
    color: '#475569',
    margin: '0 0 1.5rem 0',
    textAlign: 'center',
    lineHeight: 1.6,
  },
  previewBox: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '1.5rem',
  },
  previewRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.4rem 0',
    fontSize: '0.9rem',
    color: '#0f172a',
  },
  previewLabel: { color: '#64748b', fontWeight: 600 },
  label: {
    display: 'block',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#334155',
    marginBottom: '0.4rem',
    marginTop: '1rem',
  },
  input: {
    width: '100%',
    padding: '0.75rem 1rem',
    fontSize: '1rem',
    border: '1.5px solid #cbd5e1',
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  row: { display: 'flex', gap: '1rem' },
  turnstile: {
    display: 'flex',
    justifyContent: 'center',
    margin: '1.5rem 0 1rem 0',
    minHeight: '65px',
  },
  button: {
    width: '100%',
    padding: '0.85rem',
    background: '#2563eb',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 700,
    cursor: 'pointer',
    marginTop: '1rem',
  },
  buttonDisabled: {
    background: '#94a3b8',
    cursor: 'not-allowed',
  },
  error: {
    background: '#fef2f2',
    border: '1px solid #fca5a5',
    color: '#b91c1c',
    padding: '0.85rem 1rem',
    borderRadius: '8px',
    fontSize: '0.9rem',
    marginBottom: '1rem',
  },
  successBox: {
    background: '#f0fdf4',
    border: '1px solid #86efac',
    borderRadius: '8px',
    padding: '1rem',
    margin: '1rem 0',
    fontSize: '0.95rem',
    lineHeight: 1.8,
  },
};
